import { useCallback, useEffect, useReducer, useState } from 'react'
import { errorTextOf, pushScreenToast, wayBack } from './feedback'

// A save sent from a dialog, and what becomes of its answer.
//
// # WHY THIS EXISTS
//
// A dialog can be closed while its save is still out: the frame lets Close and
// Escape through, so nobody is held by a request that never answers. Closing
// does not cancel the request, so the answer still arrives, in a component
// whose dialog may by then be shut, or open again on another record. Left to
// itself that answer would show one record's refusal in another record's
// dialog, or shut the dialog the person has just opened.
//
// # THE RULE
//
// An answer belongs to the record its save was sent for.
//
//  - The dialog is open on that record when it arrives: the dialog deals with
//    it as it always did.
//  - The dialog is shut, or open on another record, and the save failed:
//    nothing of the dialog runs. A toast gives the reason and the way back.
//  - The same, and the save worked: the feedback net's toast says so
//    (lib/feedback.ts), and `done` runs once the dialog is shut, so the page
//    behind catches up and nothing changes under a dialog open on something
//    else.
//
// A dialog is busy only with a save for the record it is open on. Opened
// again on the same record while that save is out, it is still busy, so the
// main action cannot send the same thing twice; opened on another, it is not.
// That holds only where useSave outlives the dialog: it is called by whatever
// opens the dialog, never by a piece inside its body, which is drawn afresh
// each time the dialog opens and would start with nothing out.
//
// # A SAVE THAT NEVER ANSWERS
//
// The console's requests carry no time limit of their own (lib/api.ts), so a
// save can stay out for good, and a dialog opened again on its record would
// keep its main action off until the page was left. The wait has an end.
// WAIT_MS after a save was sent the dialog stops being busy with it and says,
// in a toast, that the earlier attempt may still arrive. It says so again each
// time it is opened on that record while the answer is still owed, because
// that is when somebody is about to try again. The request is not cancelled,
// and its answer, if it comes, is dealt with as any other.
//
// # MORE THAN ONE THING TO SEND
//
// A record's dialog may send several things that do not wait for each other:
// a finding's owner, its due date and its status; a connection's test and its
// settings. Each is an act with a name (`of`), busy only with its own saves,
// so a test that is out does not lock the form and the form does not lock the
// test. All of them are for the one record, so their answers are dealt with
// by the one rule above.
//
// The decisions are plain code so they are tested without a browser: fateOf,
// settle, and Saves, which remembers what is out. useSave holds one Saves and
// tells it what the dialog is open on.

export type SaveHandlers<T> = {
  // An answer the dialog itself shows (a key that is shown once). Only while
  // the dialog is still open on what it was sent for.
  shown?: (answer: T) => void
  // What follows a save that worked: clear the form, close, read the page
  // again.
  done?: (answer: T) => void
  failed?: (err: unknown) => void
  // What to say where `shown` could not run because the dialog had gone.
  lost?: string
}

// "here": the dialog is open on the record it was sent for. "behind": the
// dialog is shut. "wait": it is open on another record. "toast": it failed and
// the dialog is no longer on its record.
export type Fate = 'here' | 'behind' | 'wait' | 'toast'

// What a dialog is open on: any stable word for the record, or null or false
// while it is shut.
export type On = string | boolean | null

const isOpen = (on: On): on is string | true => on !== null && on !== false

export function fateOf(sent: On, now: On, worked: boolean): Fate {
  if (isOpen(now) && now === sent) return 'here'
  if (!worked) return 'toast'
  return isOpen(now) ? 'wait' : 'behind'
}

export type Outcome<T> = { ok: true; answer: T } | { ok: false; error: unknown }

export function settle<T>(
  fate: Fate,
  outcome: Outcome<T>,
  on: SaveHandlers<T>,
  env: { name: string; later: (run: () => void) => void; warn: (words: string) => void },
): void {
  if (!outcome.ok) {
    if (fate === 'here') on.failed?.(outcome.error)
    else env.warn(errorTextOf(outcome.error) + ' ' + wayBack(env.name))
    return
  }
  const { answer } = outcome
  if (fate === 'here') on.shown?.(answer)
  else if (on.shown && on.lost) env.warn(on.lost)
  if (fate === 'wait') env.later(() => on.done?.(answer))
  else on.done?.(answer)
}

// Two minutes. It is when the API stops writing an answer (WriteTimeout in
// apps/tprm-api/main.go) and when the load balancer drops a connection that
// has said nothing (idle_timeout in deploy/aws/terraform/alb.tf), so an answer
// still owed after it is not coming by the ordinary road. lib/feedback.ts ties
// a late failure to a closed dialog for the same two minutes.
export const WAIT_MS = 120000

export const LATE = 'The earlier attempt has had no answer for two minutes and may still arrive, so check before trying again.'

type Out = { record: On; act: string; late: boolean }

export type SavesEnv = {
  warn: (words: string) => void
  // Runs `run` once `ms` have passed, unless what it gives back is called
  // first.
  after: (ms: number, run: () => void) => () => void
  // Something the dialog is drawn from has changed.
  changed: () => void
}

// Which saves are out and for which record, what the dialog is open on now,
// and what is owed to the page behind once it shuts.
export class Saves {
  private record: On = null
  private out: Out[] = []
  private owed: (() => void)[] = []
  private waits = new Set<() => void>()

  constructor(private readonly env: SavesEnv) {}

  // What the dialog is open on, told each time that changes.
  on(record: On): void {
    this.record = record
    if (!isOpen(record)) for (const run of this.owed.splice(0)) run()
    else if (this.out.some((o) => o.late && o.record === record)) this.env.warn(LATE)
  }

  busy(record: On, act = ''): boolean {
    return isOpen(record) && this.out.some((o) => !o.late && o.record === record && o.act === act)
  }

  // `name` is what a toast calls the dialog. `act` is which of the things the
  // dialog can send this is, where it can send more than one.
  send<T>(work: Promise<T>, on: SaveHandlers<T>, name: string, act = ''): void {
    const save: Out = { record: this.record, act, late: false }
    this.out.push(save)
    this.env.changed()
    const stop = this.env.after(WAIT_MS, () => {
      this.waits.delete(stop)
      save.late = true
      if (isOpen(save.record) && this.record === save.record) this.env.warn(LATE)
      this.env.changed()
    })
    this.waits.add(stop)
    const end = (outcome: Outcome<T>) => {
      stop()
      this.waits.delete(stop)
      this.out = this.out.filter((o) => o !== save)
      this.env.changed()
      settle(fateOf(save.record, this.record, outcome.ok), outcome, on, {
        name,
        later: (run) => this.owed.push(run),
        warn: this.env.warn,
      })
    }
    work.then(
      (answer) => end({ ok: true, answer }),
      (error) => end({ ok: false, error }),
    )
  }

  // The component that held this has gone, and with it anybody to tell that
  // a wait has ended.
  forget(): void {
    for (const stop of this.waits) stop()
    this.waits.clear()
  }
}

export type Save = { busy: boolean; send: <T>(work: Promise<T>, on: SaveHandlers<T>) => void }

// A dialog's save, with the way to one of its acts.
export type DialogSave = Save & { of: (act: string) => Save }

// `record` is what the dialog is open on. `name` is what the toast calls the
// dialog.
export function useSave(record: On, name: string): DialogSave {
  const [, redraw] = useReducer((n: number) => n + 1, 0)
  const [saves] = useState(
    () =>
      new Saves({
        warn: (words) => pushScreenToast(words, 'warn'),
        after: (ms, run) => {
          const timer = globalThis.setTimeout(run, ms)
          return () => globalThis.clearTimeout(timer)
        },
        changed: redraw,
      }),
  )

  useEffect(() => saves.on(record), [saves, record])
  useEffect(() => () => saves.forget(), [saves])

  const send = useCallback(<T,>(work: Promise<T>, on: SaveHandlers<T>): void => saves.send(work, on, name), [saves, name])

  return {
    busy: saves.busy(record),
    send,
    of: (act) => ({ busy: saves.busy(record, act), send: (work, on) => saves.send(work, on, name, act) }),
  }
}
